import {
  breakpoints,
  Inline,
  Input,
  Stack,
  Text,
  textColors,
  textVariants,
  vars,
} from '@etg/wings';
import { useRevolut, useProperty, useTranslation } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { useRef, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SearchFormValues } from '../../../../common/search-form-panel/SearchFormContainer';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import { passengerFields, PASSENGERS_FORM_SECTION } from '../../constants/formFields';
import { useFormattedChildrenAges } from '../../utils/useFormattedChildrenAges';
import { usePassengers } from '../../utils/usePassengers';
import {
  getAdultsToggleConstraints,
  getChildrenToggleConstraints,
  getInfantsToggleConstraints,
} from '../../utils/passengers';
import PassengerRow from './PassengerRow';
import PassengersToggle from './PassengersToggle';

const wrapperStyles = css`
  min-width: 0;
`;

const passengersWrapperStyles = css`
  margin-inline: calc(${vars.dialog.contentPadding} * -1);

  @media (min-width: ${breakpoints._768}) {
    margin: 0;
  }
`;

const agesStyles = css`
  background-color: ${vars.colors.page.background.primary};
  border-top: 0.0625rem solid ${vars.colors.divider};
  padding: 0.5rem 1rem;
`;

const alternateBoxStyles = css`
  background-color: #fcfcfc;
  border-radius: ${vars.inputs.borderRadius};

  @media (prefers-color-scheme: dark) {
    background-color: #292929;
  }
`;

const textAlignCenterStyles = css`
  text-align: center;
`;

interface WrapperProps {
  ages: ReactNode;
  categories: ReactNode;
}

const DefaultWrapper = ({ ages, categories }: WrapperProps) => {
  return (
    <div className={passengersWrapperStyles}>
      {categories}
      {ages}
    </div>
  );
};

const AlternateWrapper = ({ ages, categories }: WrapperProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={24}>
      <Stack spacing={16}>
        <div className={alternateBoxStyles}>{categories}</div>
        {ages && <div className={alternateBoxStyles}>{ages}</div>}
      </Stack>
      <Inline
        align="center"
        as={Text}
        className={textAlignCenterStyles}
        color={textColors.SECONDARY}
        variant={textVariants._12}
      >
        {t('Searchform.Passengers.Infants.Information')}
      </Inline>
    </Stack>
  );
};

type PassengersProps = {
  className?: string;
  constraints?: {
    maxNumberOfTravelers: number;
    isDisabled: boolean;
  };
};

const Passengers = ({ className, constraints }: PassengersProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isRevolut } = useRevolut();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { watch } = useFormContext<SearchFormValues>();
  const values = watch(PASSENGERS_FORM_SECTION);
  const { label } = usePassengers(values);
  const childrenAgesLabel = useFormattedChildrenAges(values);
  const buttonId = 'passengers-toggle-button';
  const passengersCount = values.adults + values.children + values.infants;
  const useAlternateLayout = p('SearchForm.Passengers.AlternateInputLayout.Enabled');

  const onLabelClick = () => {
    buttonRef.current?.focus();
  };

  const Wrapper = useAlternateLayout ? AlternateWrapper : DefaultWrapper;

  return (
    <Stack className={cx(wrapperStyles, className)} spacing={4}>
      <Input.Label
        className={isRevolut ? screenReadersOnlyStyles : undefined}
        htmlFor={buttonId}
        onClick={onLabelClick}
      >
        {t('Searchform.Passengers.Label')}
      </Input.Label>
      <PassengersToggle
        buttonId={buttonId}
        childrenAgesLabel={childrenAgesLabel}
        isDisabled={constraints?.isDisabled}
        label={label}
        passengersCount={passengersCount}
        ref={buttonRef}
        title={t('Searchform.Passengers.Label')}
      >
        <Wrapper
          ages={
            values.children > 0 && (
              <>
                {!useAlternateLayout && (
                  <div className={agesStyles}>
                    {t('Responsive.Air.SearchFormPage.PassengersPane.ChildAgeInfo')}
                  </div>
                )}
                {Array.from({ length: values.children }).map((_, index) => (
                  <PassengerRow
                    constraints={constraints}
                    fieldName={`${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN_AGES}`}
                    key={`child-${index}`}
                    label={t('Air.Search.ChildAge', index + 1)}
                    passengerIndex={index}
                  />
                ))}
              </>
            )
          }
          categories={
            <>
              <PassengerRow
                ageSpan={t('Searchform.Passengers.Adults.AgeSpan.Label')}
                fieldName={`${PASSENGERS_FORM_SECTION}.${passengerFields.ADULTS}`}
                label={t('Air.Search.Adults')}
                toggleConstraints={getAdultsToggleConstraints(values, constraints)}
              />
              <PassengerRow
                ageSpan={t('Searchform.Passengers.Children.AgeSpan.Label')}
                fieldName={`${PASSENGERS_FORM_SECTION}.${passengerFields.CHILDREN}`}
                label={t('Air.Search.Children')}
                toggleConstraints={getChildrenToggleConstraints(values, constraints)}
              />
              <PassengerRow
                ageSpan={t('Searchform.Passengers.Infants.AgeSpan.Label')}
                constraints={constraints}
                description={
                  (!useAlternateLayout && t('Searchform.Passengers.Infants.Information')) ||
                  undefined
                }
                fieldName={`${PASSENGERS_FORM_SECTION}.${passengerFields.INFANTS}`}
                label={t('Air.Search.Infants')}
                toggleConstraints={getInfantsToggleConstraints(values)}
              />
            </>
          }
        />
      </PassengersToggle>
    </Stack>
  );
};

export default Passengers;
